// Client-error triage for PostHog.
//
// Roughly 95% of the client exception volume is thrown AT the page, not BY it:
// JS bridges that Meta's and Microsoft's in-app browsers inject into their
// WebView, browser extensions, link-scanning bots, and Next's own control-flow
// throws. None of it is our code, none of it is loadable or fixable from here,
// and no customer ever sees any of it. Left unfiltered it buries the crashes
// that DO break a checkout under a few hundred daily non-events.

const NOISE_PATTERNS: RegExp[] = [
  // Meta's Android in-app browser injects a native bridge and keeps calling it
  // after the WebView's Java peer is collected. Fires hardest on /checkout,
  // because that is where the pixel sends the most events.
  /sendDataToNative/,
  /Error invoking postMessage/,
  /Java object is gone/,
  /Java exception was raised/,
  // Same bridge on iOS: the injected script assumes WKWebView message handlers
  // exist on every page it is injected into, including plain Safari.
  /webkit\.messageHandlers/,
  // Android WebView autofill and the Bing app's search bridge.
  /_AutofillCallbackHandler/,
  /instantSearchSDKJSBridge/,
  // Outlook Safe Links pre-fetches order and preview links and runs its scanner
  // against a page it then tears down mid-call.
  /Object Not Found Matching Id/,
  // Browser extensions talking to a background page that is gone.
  /(?:chrome|moz|safari|safari-web|ms-browser)-extension:\/\//,
  /runtime\.sendMessage/,
  /No Listener:/,
  /Tab not found/,
  // Cross-origin script with no CORS header: the browser strips everything, so
  // there is no file, no line and no message left to act on.
  /\bScript error\.?\b/,
  // Benign: the browser aborts the extra layout pass by itself.
  /ResizeObserver loop/,
  // notFound() and redirect() are how Next renders a 404 and a redirect. They
  // travel as thrown errors and are not faults.
  /NEXT_NOT_FOUND/,
  /NEXT_REDIRECT/,
  // A DOM Event handed to the capture API instead of an Error. Carries a single
  // `isTrusted` key and no stack, so it can never be traced back to anything.
  /Event captured as exception with keys: isTrusted/,
];

/** True when the text of an exception marks it as third-party or control-flow noise. */
export function isThirdPartyNoise(text: string): boolean {
  return NOISE_PATTERNS.some((pattern) => pattern.test(text));
}

/**
 * Flattens the parts of a PostHog `$exception` event that can carry a signature
 * (type, message, and every stack frame) into one string to match against.
 * PostHog has moved this shape around between versions, so read both the modern
 * `$exception_list` and the flat legacy properties rather than pick one.
 */
export function exceptionSignature(properties: Record<string, unknown> | undefined): string {
  if (!properties) return "";
  const parts: unknown[] = [
    properties.$exception_list,
    properties.$exception_type,
    properties.$exception_message,
    properties.$exception_source,
  ];
  try {
    return parts.filter((part) => part !== undefined && part !== null).map((part) => (typeof part === "string" ? part : JSON.stringify(part))).join(" ");
  } catch {
    // A circular or unserialisable payload tells us nothing either way; let it through.
    return "";
  }
}
